import { useRef, type CSSProperties } from 'react'
import { Button, Group } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { FiCamera, FiImage, FiUpload } from 'react-icons/fi'

// react-icons v5 returns ReactNode; cast to satisfy React 17 JSX.Element constraint
const CameraIcon = FiCamera as React.FC<{ size?: number }>
const ImageIcon = FiImage as React.FC<{ size?: number }>
const UploadIcon = FiUpload as React.FC<{ size?: number }>

/** Not `display:none` — iOS Safari often blocks programmatic `.click()` on fully hidden file inputs. */
const VISUALLY_HIDDEN_INPUT: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
  opacity: 0
}

interface Props {
  onFile: (file: File) => void;
  accept?: string;
  loading?: boolean;
  label?: string;
  variant?: 'default' | 'filled' | 'light' | 'subtle' | 'outline';
  size?: string;
  fullWidth?: boolean;
  disabled?: boolean;
}

/**
 * On mobile: two explicit buttons — "Take photo" (rear camera) and "Gallery".
 * On desktop: single file picker button.
 */
export default function MobileImageCapture ({
  onFile,
  accept = 'image/jpeg,image/jpg,image/png,image/webp',
  loading = false,
  label = 'Add image',
  variant = 'default',
  size = 'sm',
  fullWidth = false,
  disabled = false
}: Props) {
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)
  const isMobile = useMediaQuery('(max-width: 768px)') ?? false

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) onFile(file)
    e.target.value = ''
  }

  return (
    <>
      {/* Hidden camera input (rear camera on mobile) */}
      <input
        ref={cameraInputRef}
        type="file"
        accept={accept}
        capture="environment"
        onChange={handleChange}
        style={VISUALLY_HIDDEN_INPUT}
        aria-hidden="true"
      />
      {/* Hidden gallery / standard file picker */}
      <input
        ref={galleryInputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        style={VISUALLY_HIDDEN_INPUT}
        aria-hidden="true"
      />

      {isMobile
        ? (
        <Group spacing="xs" noWrap>
          <Button
            variant="filled"
            color="orange"
            size={size}
            loading={loading}
            disabled={disabled}
            leftIcon={<CameraIcon size={14} />}
            onClick={() => cameraInputRef.current?.click()}
            sx={{ flex: 1 }}
          >
            Camera
          </Button>
          <Button
            variant={variant as any}
            size={size}
            loading={loading}
            disabled={disabled}
            leftIcon={<ImageIcon size={14} />}
            onClick={() => galleryInputRef.current?.click()}
            sx={{ flex: 1 }}
          >
            Gallery
          </Button>
        </Group>
          )
        : (
        <Button
          variant={variant as any}
          size={size}
          loading={loading}
          disabled={disabled}
          fullWidth={fullWidth}
          leftIcon={<UploadIcon size={14} />}
          onClick={() => galleryInputRef.current?.click()}
        >
          {label}
        </Button>
          )}
    </>
  )
}
